/**
 * Deposit Payment utilities.
 *
 * Allows collecting a partial deposit at booking, with the remainder due at completion.
 *
 * Deposit configuration stored in listing.publicData.depositPercent (number 0-100).
 * Default: 30% deposit if feature is enabled and no custom percent is set.
 *
 * Transaction flow:
 *   1. Customer pays deposit at booking (e.g., 30%)
 *   2. Remaining balance (70%) collected when booking is confirmed/completed
 *
 * Deposit amounts stored in transaction protectedData:
 *   { depositAmount: cents, remainingAmount: cents, depositPercent: number }
 */

export const DEFAULT_DEPOSIT_PERCENT = 30;
export const MIN_DEPOSIT_PERCENT = 5;
export const MAX_DEPOSIT_PERCENT = 100;

/**
 * Get deposit percent from a listing.
 * @param {Object|null} listing
 * @returns {number} — deposit percent (uses DEFAULT if not set)
 */
export const getDepositPercent = (listing) => {
  const val = listing?.attributes?.publicData?.depositPercent;
  if (val === null || val === undefined) return DEFAULT_DEPOSIT_PERCENT;
  const n = parseFloat(val);
  if (isNaN(n) || n < MIN_DEPOSIT_PERCENT || n > MAX_DEPOSIT_PERCENT) return DEFAULT_DEPOSIT_PERCENT;
  return n;
};

/**
 * Calculate deposit amounts.
 * @param {number} totalCents — total price in cents
 * @param {number} depositPercent — deposit percentage
 * @returns {{ depositCents: number, remainingCents: number, depositPercent: number }}
 */
export const calculateDeposit = (totalCents, depositPercent) => {
  const pct = Math.min(Math.max(depositPercent, 0), 100);
  const depositCents = Math.round(totalCents * (pct / 100));
  const remainingCents = totalCents - depositCents;
  return { depositCents, remainingCents, depositPercent: pct };
};

/**
 * Validate a deposit percent value.
 * @param {number|string} value
 * @returns {string|null} — error message or null
 */
export const validateDepositPercent = (value) => {
  const n = parseFloat(value);
  if (isNaN(n)) return 'Must be a number';
  if (n < MIN_DEPOSIT_PERCENT) return `Minimum deposit is ${MIN_DEPOSIT_PERCENT}%`;
  if (n > MAX_DEPOSIT_PERCENT) return `Maximum deposit is ${MAX_DEPOSIT_PERCENT}%`;
  return null;
};

/**
 * Format deposit info for display.
 * @param {number} depositCents
 * @param {number} remainingCents
 * @param {string} currency
 * @returns {{ depositFormatted: string, remainingFormatted: string }}
 */
export const formatDepositSplit = (depositCents, remainingCents, currency = 'EUR') => {
  const symbols = { EUR: '€', USD: '$', GBP: '£' };
  const symbol = symbols[currency] || currency + ' ';
  const fmt = (c) => `${symbol}${(c / 100).toFixed(2)}`;
  return {
    depositFormatted: fmt(depositCents),
    remainingFormatted: fmt(remainingCents),
  };
};

/**
 * Get deposit info from transaction protectedData.
 * @param {Object|null} transaction
 * @returns {Object|null}
 */
export const getDepositInfo = (transaction) =>
  transaction?.attributes?.protectedData?.depositInfo || null;
